import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'hsl_cookie_consent';

export type CookieCategory = 'essential' | 'functional' | 'analytics' | 'marketing';

export type CookiePreferences = {
  essential: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  acceptedAt: string;
};

type CookieConsentContextValue = {
  preferences: CookiePreferences | null;
  hasAnswered: boolean;
  isPreferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
  acceptAll: () => void;
  rejectOptional: () => void;
  savePreferences: (prefs: Omit<CookiePreferences, 'essential' | 'acceptedAt'>) => void;
  isCategoryEnabled: (category: CookieCategory) => boolean;
};

const defaultOptional = {
  functional: false,
  analytics: false,
  marketing: false,
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

function readStoredPreferences(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookiePreferences;
    if (parsed.essential !== true) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePreferences(preferences: CookiePreferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<CookiePreferences | null>(null);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  useEffect(() => {
    setPreferences(readStoredPreferences());
  }, []);

  const persist = useCallback((next: CookiePreferences) => {
    writePreferences(next);
    setPreferences(next);
    setIsPreferencesOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    persist({
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
      acceptedAt: new Date().toISOString(),
    });
  }, [persist]);

  const rejectOptional = useCallback(() => {
    persist({
      essential: true,
      ...defaultOptional,
      acceptedAt: new Date().toISOString(),
    });
  }, [persist]);

  const savePreferences = useCallback(
    (prefs: Omit<CookiePreferences, 'essential' | 'acceptedAt'>) => {
      persist({
        essential: true,
        functional: prefs.functional,
        analytics: prefs.analytics,
        marketing: prefs.marketing,
        acceptedAt: new Date().toISOString(),
      });
    },
    [persist],
  );

  const isCategoryEnabled = useCallback(
    (category: CookieCategory) => {
      if (category === 'essential') return true;
      if (!preferences) return false;
      return preferences[category];
    },
    [preferences],
  );

  const value = useMemo(
    () => ({
      preferences,
      hasAnswered: preferences !== null,
      isPreferencesOpen,
      openPreferences: () => setIsPreferencesOpen(true),
      closePreferences: () => setIsPreferencesOpen(false),
      acceptAll,
      rejectOptional,
      savePreferences,
      isCategoryEnabled,
    }),
    [
      preferences,
      isPreferencesOpen,
      acceptAll,
      rejectOptional,
      savePreferences,
      isCategoryEnabled,
    ],
  );

  return (
    <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
}
